import type { OpaqueData, PaymentConfig } from './api';

/**
 * Accept.js: Authorize.net's card tokeniser.
 *
 * The card number, expiry and code go from these inputs straight to
 * Authorize.net, which answers with a one-time token good for fifteen minutes.
 * Only the token reaches our API. That keeps the card itself out of every
 * request, log and audit row this system writes.
 *
 * The script is loaded on demand from their host -- they require it, and a
 * vendored copy would stop matching their servers the day they change it -- and
 * only on the billing screen, so nothing else in the console depends on a third
 * party being up.
 */

interface AcceptResponse {
  opaqueData?: OpaqueData;
  messages: { resultCode: 'Ok' | 'Error'; message: { code: string; text: string }[] };
}

interface AcceptGlobal {
  dispatchData: (
    secureData: {
      authData: { clientKey: string; apiLoginID: string };
      cardData: {
        cardNumber: string;
        month: string;
        year: string;
        cardCode: string;
        zip?: string;
        fullName?: string;
      };
    },
    callback: (response: AcceptResponse) => void,
  ) => void;
}

declare global {
  interface Window {
    Accept?: AcceptGlobal;
  }
}

let loading: Promise<AcceptGlobal> | null = null;
let loadedFrom: string | null = null;

function load(url: string): Promise<AcceptGlobal> {
  if (loading && loadedFrom === url) return loading;
  loadedFrom = url;
  loading = new Promise<AcceptGlobal>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.charset = 'utf-8';
    script.async = true;
    script.onload = () => {
      if (window.Accept) resolve(window.Accept);
      else reject(new Error('The payment form did not load. Reload the page and try again.'));
    };
    script.onerror = () => {
      loading = null;
      reject(new Error('Could not reach Authorize.net to load the payment form.'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** Start loading early, so the token round trip is the only wait on submit. */
export function preloadAccept(config: PaymentConfig): void {
  if (config.enabled) void load(config.acceptJsUrl).catch(() => {});
}

export interface CardInput {
  number: string;
  /** MM/YY or MM/YYYY. */
  expiry: string;
  code: string;
  zip: string;
  fullName: string;
}

export async function tokeniseCard(config: PaymentConfig, card: CardInput): Promise<OpaqueData> {
  if (!config.enabled || !config.clientKey || !config.apiLoginId) {
    throw new Error('Card payments are not set up yet. Contact support@allrouted.com.');
  }
  const accept = await load(config.acceptJsUrl);
  const [month = '', yearRaw = ''] = card.expiry.split('/').map((p) => p.trim());
  const year = yearRaw.length === 2 ? `20${yearRaw}` : yearRaw;

  return new Promise<OpaqueData>((resolve, reject) => {
    accept.dispatchData(
      {
        authData: { clientKey: config.clientKey!, apiLoginID: config.apiLoginId! },
        cardData: {
          cardNumber: card.number.replace(/\D/g, ''),
          month: month.padStart(2, '0'),
          year,
          cardCode: card.code.replace(/\D/g, ''),
          zip: card.zip.trim() || undefined,
          fullName: card.fullName.trim() || undefined,
        },
      },
      (response) => {
        if (response.messages.resultCode === 'Ok' && response.opaqueData) {
          resolve(response.opaqueData);
          return;
        }
        const text = response.messages.message.map((m) => m.text).join(' ');
        reject(new Error(text || 'The card details were not accepted.'));
      },
    );
  });
}

/* ------------------------------------------------------------------ *
 * Formatting while typing
 * ------------------------------------------------------------------ */

export type CardBrand = 'visa' | 'mastercard' | 'amex' | 'discover' | 'unknown';

export function brandOf(number: string): CardBrand {
  const n = number.replace(/\D/g, '');
  if (/^4/.test(n)) return 'visa';
  if (/^(5[1-5]|2[2-7])/.test(n)) return 'mastercard';
  if (/^3[47]/.test(n)) return 'amex';
  if (/^(6011|65|64[4-9])/.test(n)) return 'discover';
  return 'unknown';
}

export function formatCardNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 19);
  if (brandOf(digits) === 'amex') {
    return [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10, 15)].filter(Boolean).join(' ');
  }
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

export function formatExpiry(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

/** Luhn, so a mistyped digit is caught before the round trip. */
export function luhnValid(number: string): boolean {
  const digits = number.replace(/\D/g, '');
  if (digits.length < 12) return false;
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let d = Number(digits[i]);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

export function expiryValid(expiry: string): boolean {
  const m = /^(\d{2})\/(\d{2})$/.exec(expiry);
  if (!m) return false;
  const month = Number(m[1]);
  const year = 2000 + Number(m[2]);
  if (month < 1 || month > 12) return false;
  const now = new Date();
  return year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1);
}
